import clsx from "clsx";
import { useCallback, useRef } from "react";

interface AdminTokenSearchInputProps {
  tokens: string[];
  draft: string;
  onTokensChange: (tokens: string[]) => void;
  onDraftChange: (draft: string) => void;
  placeholder?: string;
  className?: string;
}

export function AdminTokenSearchInput({
  tokens,
  draft,
  onTokensChange,
  onDraftChange,
  placeholder = "Search…",
  className,
}: AdminTokenSearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const commitDraft = useCallback(() => {
    const next = draft.trim();
    if (!next) return;
    if (!tokens.includes(next)) {
      onTokensChange([...tokens, next]);
    }
    onDraftChange("");
  }, [draft, onTokensChange, onDraftChange, tokens]);

  const removeToken = (index: number) => {
    onTokensChange(tokens.filter((_, i) => i !== index));
  };

  return (
    <div
      className={clsx(
        "flex flex-wrap items-center gap-1.5 rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 min-h-[42px]",
        className,
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {tokens.map((token, index) => (
        <span
          key={`${token}-${index}`}
          className="inline-flex items-center gap-1 max-w-full rounded-md bg-kingdom-mid/80 border border-kingdom-glow/30 pl-2 pr-1 py-0.5 text-xs text-kingdom-glow"
        >
          <span className="truncate">{token}</span>
          <button
            type="button"
            aria-label={`Remove ${token}`}
            onClick={(e) => {
              e.stopPropagation();
              removeToken(index);
            }}
            className="shrink-0 w-5 h-5 rounded text-gray-400 hover:text-white hover:bg-kingdom-dark/80"
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="search"
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitDraft();
          }
          if (e.key === "Backspace" && !draft && tokens.length > 0) {
            onTokensChange(tokens.slice(0, -1));
          }
        }}
        placeholder={tokens.length === 0 ? placeholder : "Add filter…"}
        className="flex-1 min-w-[8rem] bg-transparent border-0 outline-none text-sm text-white placeholder:text-gray-500 py-1 px-1"
      />
      <button
        type="button"
        title="Add search term"
        onClick={(e) => {
          e.stopPropagation();
          commitDraft();
        }}
        disabled={!draft.trim()}
        className="shrink-0 w-8 h-8 rounded-lg border border-kingdom-mid text-kingdom-glow font-bold text-lg leading-none disabled:opacity-30 hover:bg-kingdom-mid/50"
      >
        +
      </button>
    </div>
  );
}

/** Space-separated terms for API (each term must match — AND). */
export function adminSearchQueryFromTokens(tokens: string[], draft: string): string {
  return [...tokens, draft.trim()].filter(Boolean).join(" ");
}
